import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of, Subscriber } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/auth/services/authentication.service';
import { AddStopMarkerService } from 'src/app/services/add-stop-marker.service';
import { PlaceSetService } from 'src/app/services/place-set.service';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import { PlaceSetType } from 'src/app/_models/state/placeSetState';
import { UIPlaceState } from 'src/app/_models/state/placeState';
import { ModalboxComponent } from '../modalbox/modalbox.component';
import { StopLocation } from 'src/app/_models/state/stopState';
import { SingleSearchLocation } from 'src/app/_models/pcmws/singleSearch/singleSearchResponse';
import { deleteFavoriteModalOptions } from 'src/app/_helpers/create-modal-options';
import { GoogleAnalytics } from 'src/app/services/google-analytics.service';

interface TextStyle {
  textClass: string;
  textValue: string;
  icon?: string;
  iconpath?: string;
  count?: number;
}
export interface ListItem {
  id?: number;
  searchText?: string;
  toolTip?: string;
  primary?: TextStyle;
  secondary?: TextStyle;
  tertiary?: TextStyle;
  child?: ListItem[];
  isSelected?: boolean;
}

@Component({
  selector: 'app-favorite-place',
  templateUrl: './favorite-place.component.html',
  styleUrls: ['./favorite-place.component.scss'],
})
export class FavoritePlaceComponent implements OnInit, OnDestroy {
  @Output() selectedPlace = new EventEmitter<SingleSearchLocation>();
  @Input() isFavorite: boolean;
  @ViewChild('search') search: ElementRef;
  places: UIPlaceState[];
  isLoadingResults: boolean;
  placeLists: Observable<UIPlaceState[]>;
  private sm = new SubscriptionManager();
  selectedValue: string;
  hasResult: boolean;
  hasAdminRole$: Observable<boolean>;

  constructor(
    private placeSetService: PlaceSetService,
    private authenticationService: AuthenticationService,
    public modalService: BsModalService,
    private addStopMarkerService: AddStopMarkerService,
    private googleAnalytics: GoogleAnalytics
  ) {}

  ngOnInit(): void {
    this.getFavoritePlaces();
    this.hasAdminRole$ = this.authenticationService.currentUser.pipe(
      map((user) => user.roles.some((role) => role === 'Company Administrator'))
    );
  }

  getPlacesAsObservable(token: string): Observable<UIPlaceState[]> {
    const query = new RegExp(token, 'i');
    this.hasResult = this.selectedValue
      ? this.places.some((val) =>
          val?.DisplayText?.toLowerCase().includes(
            this.selectedValue.toLowerCase()
          )
        )
      : true;
    let result: UIPlaceState[];
    if (this.hasResult) {
      result = this.places.filter((place: UIPlaceState) => {
        return query.test(place.DisplayText);
      });
    } else {
      result = [
        {
          PlaceName: 'No Match Results Found',
        },
      ];
    }
    return of(result);
  }

  getFavoritePlaces(removeFocus = true): void {
    this.isLoadingResults = true;
    this.sm.manage = this.placeSetService.favoritePlaceStore$.subscribe(
      (combinedList) => {
        if (combinedList) {
          this.isLoadingResults = false;
          this.places = combinedList?.length
            ? combinedList
            : [
                {
                  PlaceName: 'No Favorites Found',
                },
              ];
          this.placeLists = this.getSearchableList(this.places);
          if (removeFocus) {
            setTimeout(() => this.search?.nativeElement?.focus());
          }
        }
      },
      (err) => {
        this.placeLists = of([
          {
            PlaceName: 'Error Occured',
          },
        ]);
        setTimeout(() => this.search?.nativeElement?.focus());
        this.isLoadingResults = false;
      }
    );
  }

  getSearchableList(items: UIPlaceState[]): Observable<UIPlaceState[]> {
    return new Observable((observer: Subscriber<string>) => {
      // Runs on every search
      observer.next(this.selectedValue);
    }).pipe(mergeMap((token: string) => this.getPlacesAsObservable(token)));
  }

  deleteFavoriteLocation(item: UIPlaceState) {
    this.googleAnalytics.addGA(
      'Remove_Favorite',
      'Delete_Favorite_Search',
      'Favourite Search',
      'delete favorite stop from favourite search'
    );
    this.openDialog(item);
  }

  openDialog(place: UIPlaceState): void {
    const options = deleteFavoriteModalOptions({
      id: place?.Id,
      name: place?.PlaceName,
    });
    const dialogRef = this.modalService.show<ModalboxComponent>(
      ModalboxComponent,
      options
    );
    dialogRef.content.event.subscribe((id: number) => {
      this.sm.manage = this.placeSetService
        .deletePlace(id)
        .pipe(
          map(() => {
            const stopLocation: StopLocation = {
              name: place?.PlaceName,
              address: {
                streetAddress: place?.Address?.StreetAddress,
                city: place?.Address?.City,
                stateAbbreviation: place?.Address?.State,
                zip: place?.Address?.Zip,
                county: place?.Address?.County,
                countryAbbreviation: place?.Address?.Country,
              },
              coords: [
                Number.parseFloat(place?.Coords.Lon),
                Number.parseFloat(place?.Coords.Lat),
              ],
              isFavorite: false,
              favoriteId: place?.Id,
              isSharedFavorite: false,
            };
            this.addStopMarkerService.updateMarker(stopLocation);
          })
        )
        .subscribe();
    });
  }

  showLocation(place: UIPlaceState) {
    const stopLocation: StopLocation = {
      name: place?.PlaceName,
      address: {
        streetAddress: place?.Address?.StreetAddress,
        city: place?.Address?.City,
        stateAbbreviation: place?.Address?.State,
        zip: place?.Address?.Zip,
        county: place?.Address?.County,
        countryAbbreviation: place?.Address?.Country,
      },
      coords: [
        Number.parseFloat(place?.Coords.Lon),
        Number.parseFloat(place?.Coords.Lat),
      ],
      isFavorite: place.Type === PlaceSetType.Favorite,
      favoriteId: place?.Id,
      isSharedFavorite: place?.IsShared,
    };
    this.addStopMarkerService.addMarker(stopLocation);
    this.googleAnalytics.addGA(
      'Show_Favorite_Location',
      'Favorite_Location_Show',
      'Favourite Search',
      'show location selected via favourite search'
    );
  }

  onSelect(event): void {
    const selectedLocation: UIPlaceState = event?.item;
    if (selectedLocation) {
      const location: SingleSearchLocation = {
        Address: {
          StreetAddress: selectedLocation.Address?.StreetAddress,
          City: selectedLocation.Address?.City,
          State: selectedLocation.Address?.State,
          StateName: selectedLocation.Address?.StateName,
          Zip: selectedLocation.Address?.Zip,
          County: selectedLocation.Address?.County,
          Country: selectedLocation.Address?.Country,
          SPLC: selectedLocation.Address?.SPLC,
        },
        Coords: selectedLocation?.Coords,
        PlaceId: selectedLocation?.Id?.toString(),
        PlaceName: selectedLocation?.PlaceName,
      };
      this.selectedPlace.emit(location);
    }
    this.search.nativeElement.value = '';
    this.googleAnalytics.addGA(
      'Add_Stop',
      'Add_Stop_Favorite_Search',
      'Favourite Search',
      'Add stop by Favourite search'
    );
  }

  ngOnDestroy(): void {
    this.sm.unsubscribe();
  }
}
